import { useState, useMemo, useCallback } from 'react'
import { type ColumnDef } from '@tanstack/react-table'
import * as XLSX from 'xlsx'
import { ExtractionTable } from '@/components/extraction/ExtractionTable'
import { CitationLink } from '@/components/extraction/CitationLink'
import { CountdownBadge } from '@/components/extraction/CountdownBadge'
import { NotePopover } from '@/components/extraction/NotePopover'
import { RowActionsMenu } from '@/components/extraction/RowActionsMenu'
import { AddItemModal, type ModalFieldConfig } from '@/components/extraction/AddItemModal'
import { BulkActionsBar } from '@/components/ui/bulk-actions-bar'
import { BulkConfirmDelete } from '@/components/extraction/BulkConfirmDelete'
import { BulkValuePicker } from '@/components/ui/bulk-value-picker'
import { useTableFilterBar } from '@/components/extraction/TableFilterBar'
import { KeyDateEditPopover } from '@/components/extraction/KeyDateEditPopover'
import { KeyDatesTimelineView } from '@/components/extraction/KeyDatesTimelineView'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Pencil, Trash2 } from 'lucide-react'
import { useTableFilters } from '@/hooks/use-table-filters'
import { useAuthStore } from '@/stores/auth-store'
import {
  keyDatesViewModeStorageKey,
  readKeyDatesViewMode,
  type KeyDatesViewMode,
} from '@/lib/key-dates-timeline'
import { addItemWithFeedback } from '@/lib/add-item-feedback'
import { buildExportSheet, exportScopeNote } from '@/lib/export-provenance'
import type { FilterGroups, FilterOptionDef } from '@/lib/table-filters'
import { useExtractionStore } from '@/stores/extraction-store'
import { useBidsStore } from '@/stores/bids-store'
import type { ExtractedDate, KeyDatePhase } from '@/types/extraction'
import { KEY_DATE_PHASE_LABELS, KEY_DATE_PHASE_COLORS } from '@/types/extraction'
import { cn } from '@/lib/utils'
import { formatSourceWithPage } from '@/lib/citation-preview'
import { extractedDeadlineToApply } from '@/lib/bid-deadline'
import { toast } from 'sonner'

// ===================
// Types
// ===================

interface KeyDatesExtractionProps {
  bidId: string
  readOnly?: boolean
}

// ===================
// Filters
// ===================

/**
 * Derived from the label map, never hand-listed: a new `KeyDatePhase` must not
 * be silently unselectable here. `Object.keys` preserves declaration order, so
 * the dropdown reads chronologically (Pre-Tender → Contractual).
 */
export const KEY_DATE_PHASE_OPTIONS: FilterOptionDef[] = (
  Object.keys(KEY_DATE_PHASE_LABELS) as KeyDatePhase[]
).map((phase) => ({ value: phase, label: KEY_DATE_PHASE_LABELS[phase] }))

export function buildKeyDatesFilterGroups(): FilterGroups<ExtractedDate> {
  return {
    phase: { label: 'Phase', options: KEY_DATE_PHASE_OPTIONS, accessor: (d) => d.phase },
  }
}

// ===================
// Helpers
// ===================

function formatDate(isoDate: string): string {
  try {
    const d = new Date(isoDate)
    return d.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return isoDate
  }
}

function formatDateFallback(item: ExtractedDate): string {
  if (item.dateIso) {
    return formatDate(item.dateIso)
  }

  if (item.dateText.trim().length > 0) {
    return item.dateText
  }

  return 'No explicit date captured'
}

// ===================
// Component
// ===================

export function KeyDatesExtraction({ bidId, readOnly }: KeyDatesExtractionProps) {
  const [showAddRow, setShowAddRow] = useState(false)

  // Timeline / Table. Held in state so the switch is instant, mirrored into
  // localStorage so it survives the remount every sub-tab return causes.
  const userId = useAuthStore((s) => s.user?.id)
  const viewModeKey = keyDatesViewModeStorageKey(userId, bidId)
  const [viewMode, setViewMode] = useState<KeyDatesViewMode>(() => readKeyDatesViewMode(viewModeKey))
  // Adjust during render, not in an effect: the key changes when the bid
  // changes (React Router reuses this element across `:bidId`) and when auth
  // resolves the user, and a one-frame flash of the wrong view is worse than
  // no animation at all. Same pattern as `useTableFilters`.
  const [prevViewModeKey, setPrevViewModeKey] = useState(viewModeKey)
  if (prevViewModeKey !== viewModeKey) {
    setPrevViewModeKey(viewModeKey)
    setViewMode(readKeyDatesViewMode(viewModeKey))
  }

  const handleViewModeChange = useCallback(
    (next: string) => {
      const mode: KeyDatesViewMode = next === 'table' ? 'table' : 'timeline'
      setViewMode(mode)
      try {
        window.localStorage.setItem(viewModeKey, mode)
      } catch {
        // Private mode / storage disabled — the choice just won't persist.
      }
    },
    [viewModeKey]
  )

  const filterGroups = useMemo(() => buildKeyDatesFilterGroups(), [])
  const filters = useTableFilters<ExtractedDate>({
    bidId,
    tableKey: 'key_dates',
    groups: filterGroups,
  })

  const [bulkDelete, setBulkDelete] = useState<{ ids: string[]; aiCount: number } | null>(null)
  const [pendingClear, setPendingClear] = useState<(() => void) | null>(null)

  // Store
  const categoryState = useExtractionStore((s) => s.categories.key_dates)
  const addManualItem = useExtractionStore((s) => s.addManualItem)
  const updateItemNote = useExtractionStore((s) => s.updateItemNote)
  const deleteItem = useExtractionStore((s) => s.deleteItem)
  const bulkPatchItems = useExtractionStore((s) => s.bulkPatchItems)
  const bulkDeleteItems = useExtractionStore((s) => s.bulkDeleteItems)

  const items = categoryState.items as ExtractedDate[]

  // Current bid — for reconciling the extracted submission deadline with the bid record
  const bid = useBidsStore((s) =>
    s.selectedBid?.id === bidId ? s.selectedBid : s.bids.find((b) => b.id === bidId) ?? null
  )
  const updateBid = useBidsStore((s) => s.updateBid)

  // Returns the extracted deadline Date when it should be offered as the bid deadline:
  // absolute parseable date, bid loaded, and >24h apart from (or missing) the bid's deadline
  const getDeadlineMismatch = useCallback(
    (item: ExtractedDate): Date | null => {
      if (!item.isSubmissionDeadline || !bid) return null
      return extractedDeadlineToApply(item.dateIso, bid.deadline ?? null)
    },
    [bid]
  )

  const handleSetAsDeadline = useCallback(
    async (item: ExtractedDate) => {
      const extracted = getDeadlineMismatch(item)
      if (!extracted) return
      try {
        await updateBid({ id: bidId, deadline: extracted.toISOString() })
        toast.success('Bid deadline updated')
      } catch {
        toast.error('Failed to update bid deadline')
      }
    },
    [bidId, getDeadlineMismatch, updateBid]
  )

  // Sort: submission deadline first, then chronological
  const sortedItems = useMemo(() => {
    const deadlines = items.filter((d) => d.isSubmissionDeadline)
    const others = items
      .filter((d) => !d.isSubmissionDeadline)
      .sort((a, b) => {
        if (a.dateIso && b.dateIso) return a.dateIso.localeCompare(b.dateIso)
        if (a.dateIso) return -1
        if (b.dateIso) return 1
        return 0
      })
    return [...deadlines, ...others]
  }, [items])

  // Hoisted because the hook returns a fresh object every render: depending on
  // `filters` itself would re-run these on every render, while these two members
  // are stable (`clearAll`) or memoised on the values (`matches`).
  const { matches: filtersMatch, clearAll: clearAllFilters } = filters

  // Phase filtering
  const filteredItems = useMemo(
    () => sortedItems.filter(filtersMatch),
    [sortedItems, filtersMatch]
  )

  // Stats
  const stats = useMemo(() => {
    const count = items.length
    const deadline = items.find((d) => d.isSubmissionDeadline)
    let deadlineText = ''
    if (deadline?.dateIso) {
      const target = new Date(deadline.dateIso)
      const now = new Date()
      const targetDay = new Date(target.getFullYear(), target.getMonth(), target.getDate())
      const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const days = Math.round((targetDay.getTime() - nowDay.getTime()) / (1000 * 60 * 60 * 24))
      if (days > 0) {
        deadlineText = `Submission due in ${days} day${days !== 1 ? 's' : ''}`
      } else if (days === 0) {
        deadlineText = 'Submission due today'
      } else {
        deadlineText = `Submission was ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} ago`
      }
    }
    return { count, deadlineText }
  }, [items])

  /**
   * One count line, both views. In table mode the argument is the row count
   * AFTER the search box inside `ExtractionTable` has run (only that component
   * knows it); in timeline mode there is no search, so it is the filtered
   * count. Same wording either way, so switching views never looks like the
   * data changed.
   */
  const renderStats = useCallback(
    (visibleCount: number) => (
      <div className="flex items-center gap-2 text-meta">
        <span>
          {visibleCount === items.length
            ? `${items.length} dates`
            : `${visibleCount} of ${items.length} dates`}
        </span>
        {stats.deadlineText && (
          <>
            <span>·</span>
            <span className="font-medium text-foreground">{stats.deadlineText}</span>
          </>
        )}
      </div>
    ),
    [items.length, stats.deadlineText]
  )

  // Columns
  const columns = useMemo<ColumnDef<ExtractedDate, unknown>[]>(
    () => [
      {
        id: 'number',
        header: '#',
        size: 40,
        cell: ({ row }) =>
          row.original.isSubmissionDeadline ? (
            <span className="text-amber-500 font-medium">★</span>
          ) : (
            <span className="text-meta">{row.index + 1}</span>
          ),
      },
      {
        accessorKey: 'event',
        header: 'Event',
        size: 200,
        cell: ({ row }) => (
          <div title={row.original.description || undefined}>
            <span className={cn('text-sm', row.original.isSubmissionDeadline && 'font-medium')}>
              {row.original.event}
            </span>
            {row.original.description && (
              <p className="text-meta line-clamp-1 mt-0.5">
                {row.original.description}
              </p>
            )}
          </div>
        ),
      },
      {
        id: 'phase',
        header: 'Phase',
        size: 110,
        cell: ({ row }) => (
          <span className={cn(
            'inline-flex items-center rounded-lg px-2 py-0.5 text-xs',
            KEY_DATE_PHASE_COLORS[row.original.phase ?? 'tender']
          )}>
            {KEY_DATE_PHASE_LABELS[row.original.phase ?? 'tender']}
          </span>
        ),
      },
      {
        id: 'date',
        header: 'Date',
        size: 160,
        cell: ({ row }) => {
          const item = row.original
          const hasConcreteDate = !!item.dateIso
          const showSetAsDeadline = !readOnly && getDeadlineMismatch(item) !== null
          return (
            <div className="flex flex-col items-start gap-1">
              <div className="flex items-center gap-1.5">
                <span className={cn('text-sm', !hasConcreteDate && 'italic text-muted-foreground')}>
                  {formatDateFallback(item)}
                </span>
                {item.userTouched && <span className="text-meta">set manually</span>}
                {!readOnly && item.id && (
                  <KeyDateEditPopover
                    item={item}
                    bidId={bidId}
                    trigger={
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        aria-label="Edit date"
                        data-no-row-expand
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    }
                  />
                )}
              </div>
              {showSetAsDeadline && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2.5 text-xs"
                  onClick={(e) => {
                    e.stopPropagation()
                    void handleSetAsDeadline(row.original)
                  }}
                >
                  Set as bid deadline
                </Button>
              )}
            </div>
          )
        },
      },
      {
        accessorKey: 'time',
        header: 'Time',
        size: 110,
        cell: ({ row }) => (
          <span className="text-meta">
            {row.original.time ?? '—'}
          </span>
        ),
      },
      {
        id: 'countdown',
        header: 'Countdown',
        size: 110,
        cell: ({ row }) => (
          row.original.dateIso ? (
            <CountdownBadge dateIso={row.original.dateIso} />
          ) : row.original.dateText.trim().length > 0 ? (
            <span className="text-meta">Relative</span>
          ) : (
            <span className="text-meta">—</span>
          )
        ),
      },
      {
        id: 'source',
        header: 'Source',
        size: 180,
        cell: ({ row }) => (
          <CitationLink
            page={row.original.sourcePage}
            documentId={row.original.sourceDocumentId ?? null}
            documentName={row.original.sourceDocument || null}
            sourceQuote={row.original.sourceQuote}
            sourceHighlightData={row.original.sourceHighlightData ?? null}
          />
        ),
      },
      {
        id: 'note',
        header: 'Note',
        size: 50,
        cell: ({ row }) => (
          <NotePopover
            note={row.original.note ?? null}
            disabled={readOnly}
            onSave={(note) => {
              if (row.original.id) {
                void updateItemNote(
                  bidId,
                  'key_dates',
                  row.original.id,
                  note
                )
              }
            }}
          />
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 40,
        cell: ({ row }) => {
          const canDelete =
            !readOnly && row.original.extractionSource === 'manual' && !!row.original.id
          return (
            <RowActionsMenu
              source={row.original.extractionSource ?? 'ai'}
              sourceQuote={row.original.sourceQuote}
              {...(canDelete && {
                onDelete: () => void deleteItem(bidId, 'key_dates', row.original.id!),
              })}
            />
          )
        },
      },
    ],
    [bidId, deleteItem, updateItemNote, readOnly, getDeadlineMismatch, handleSetAsDeadline]
  )

  // Export
  const handleExport = useCallback(() => {
    const exportData = filteredItems.map((item, i) => ({
      '#': item.isSubmissionDeadline ? '★' : i + 1,
      Event: item.event,
      Description: item.description || '',
      Phase: KEY_DATE_PHASE_LABELS[item.phase ?? 'tender'],
      Date: item.dateIso ? formatDate(item.dateIso) : item.dateText,
      Time: item.time || '',
      'Submission Deadline': item.isSubmissionDeadline ? 'Yes' : '',
      Source: item.sourceDocument ? formatSourceWithPage(item.sourceDocument, item.sourcePage) : '',
    }))
    // The sheet renumbers from 1, so a filtered export is indistinguishable
    // from the full list once forwarded. Filters persist per bid, so the
    // narrowing may have been set weeks ago and forgotten.
    const worksheet = buildExportSheet(
      exportData,
      exportScopeNote({
        chips: filters.activeChips,
        shown: filteredItems.length,
        total: items.length,
      })
    )
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Key Dates')
    XLSX.writeFile(workbook, `key-dates-${new Date().toISOString().split('T')[0]}.xlsx`)
  }, [filteredItems, filters.activeChips, items.length])

  // Add manual item
  const handleAddManualItem = useCallback(
    async (values: Record<string, string | boolean>) => {
      const asString = (k: string) => {
        const v = values[k]
        return typeof v === 'string' && v ? v : null
      }
      await addItemWithFeedback(
        'Key date',
        () =>
          addManualItem(bidId, 'key_dates', {
            event: asString('event') ?? '',
            description: asString('description'),
            dateIso: asString('date'),
            dateText: asString('date') ?? '',
            time: asString('time'),
            isSubmissionDeadline: values['isSubmissionDeadline'] === true,
            phase: (asString('phase') as 'pre_tender' | 'tender' | 'post_award' | 'construction' | 'contractual') ?? 'tender',
            note: asString('note'),
            sourceQuote: '',
            sourceDocument: '',
            sourcePage: 0,
            extractionSource: 'manual',
          }),
        {
          isVisible: (created) => filtersMatch(created as ExtractedDate),
          onShowItem: clearAllFilters,
        }
      )
    },
    [bidId, addManualItem, filtersMatch, clearAllFilters]
  )

  const modalFields: ModalFieldConfig[] = useMemo(() => [
    { key: 'event', label: 'Event', type: 'text', required: true, fullWidth: true },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'date', label: 'Date', type: 'date' },
    { key: 'time', label: 'Time', type: 'time' },
    { key: 'phase', label: 'Phase', type: 'select', options: [
      { value: 'pre_tender', label: 'Pre-Tender' },
      { value: 'tender', label: 'Tender' },
      { value: 'post_award', label: 'Post-Award' },
      { value: 'construction', label: 'Construction' },
      { value: 'contractual', label: 'Contractual' },
    ], defaultValue: 'tender' },
    { key: 'isSubmissionDeadline', label: 'Submission deadline', type: 'checkbox', placeholder: 'Mark as the tender submission deadline' },
    { key: 'note', label: 'Note', type: 'textarea', fullWidth: true },
  ], [])

  // Global filter
  const globalFilterFn = useCallback((row: ExtractedDate, filterValue: string) => {
    const search = filterValue.toLowerCase()
    return (
      row.event.toLowerCase().includes(search) ||
      (row.description?.toLowerCase().includes(search) ?? false) ||
      row.dateText.toLowerCase().includes(search) ||
      KEY_DATE_PHASE_LABELS[row.phase ?? 'tender'].toLowerCase().includes(search)
    )
  }, [])

  /**
   * Is there settled, extracted data to plot?
   *
   * The timeline draws rows and nothing else. Every other thing this tab has to
   * be able to show — the "Extract Key Dates" call to action on a bid that has
   * never been analysed, the phase stepper, the streaming row preview, the
   * dropped-stream recovery, the failure banner over a previous run's rows —
   * lives inside `ExtractionTable`. Rendering the timeline through those states
   * strands the user: a never-extracted bid showed a bare "No dated events
   * yet." with no way to start a run, and a run in flight or failed was
   * invisible.
   *
   * So the timeline is gated on real data, and everything else falls through to
   * the table, which already renders each of those states. `streamError` and
   * `isRecovering` are read for truthiness, not `=== null`: they are optional
   * on the category and are legitimately `undefined` before a first run.
   */
  const canPlotTimeline =
    categoryState.state === 'populated' &&
    items.length > 0 &&
    !categoryState.isRecovering &&
    !categoryState.streamError

  // The fall-through is a render decision only. The stored preference is left
  // exactly as the user set it, so the timeline comes back by itself once the
  // run finishes — nothing here writes to localStorage.
  const showTimeline = viewMode === 'timeline' && canPlotTimeline

  // One call, rendered in whichever view is up — the chips must read the same
  // in both, and the hook behind them is per bid, not per view. Two nodes: the
  // dropdowns sit inline, the chip rail gets its own full-width row beneath.
  const filterBar = useTableFilterBar({
    groups: filterGroups,
    values: filters.values,
    activeChips: filters.activeChips,
    onToggle: filters.toggle,
    onClear: filters.clear,
    onClearAll: filters.clearAll,
  })

  return (
    <>
    {/*
      Host-rendered, above both views: the table's own toolbar lives inside
      `ExtractionTable` and does not exist at all in timeline mode.
    */}
    <div className="flex items-center justify-between gap-2 mb-3">
      <Tabs value={viewMode} onValueChange={handleViewModeChange}>
        <TabsList className="bg-muted p-1 h-9 rounded-lg">
          <TabsTrigger
            value="timeline"
            className="h-7 px-3 rounded-md data-[state=active]:bg-card data-[state=active]:shadow-sm"
          >
            Timeline
          </TabsTrigger>
          <TabsTrigger
            value="table"
            className="h-7 px-3 rounded-md data-[state=active]:bg-card data-[state=active]:shadow-sm"
          >
            Table
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>

    {showTimeline ? (
      <div className="space-y-3">
        {/* Timeline mode has no ExtractionTable toolbar, so this view hosts both
            rows itself — same stacking, same 8px gap. */}
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-2 flex-wrap">{filterBar.controls}</div>
          {filterBar.chips}
        </div>
        {renderStats(filteredItems.length)}
        <KeyDatesTimelineView
          bidId={bidId}
          items={filteredItems}
          readOnly={readOnly ?? false}
        />
      </div>
    ) : (
    <ExtractionTable<ExtractedDate>
      bidId={bidId}
      category="key_dates"
      title="Key Dates"
      description="Extract important dates, deadlines, and milestones from tender documents."
      columns={columns}
      data={filteredItems}
      readOnly={readOnly ?? false}
      onExport={handleExport}
      expandableRows
      {...(!readOnly ? { onAddManualItem: () => setShowAddRow(true) } : {})}
      enableSelection={!readOnly}
      renderBulkActions={(rows, clear) => {
        const ids = rows.map((r) => r.id!).filter(Boolean)
        const aiCount = rows.filter((r) => r.extractionSource !== 'manual').length
        return (
          <BulkActionsBar count={rows.length} onClear={clear}>
            <BulkValuePicker
              label="Phase"
              options={[
                { value: 'pre_tender', label: 'Pre-Tender' },
                { value: 'tender', label: 'Tender' },
                { value: 'post_award', label: 'Post-Award' },
                { value: 'construction', label: 'Construction' },
                { value: 'contractual', label: 'Contractual' },
              ]}
              onSelect={(phase) => {
                void (async () => {
                  await bulkPatchItems(bidId, 'key_dates', ids, { phase })
                  clear()
                })()
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-background hover:bg-destructive hover:text-destructive-foreground gap-1"
              onClick={() => {
                setBulkDelete({ ids, aiCount })
                setPendingClear(() => clear)
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
          </BulkActionsBar>
        )
      }}
      globalFilterFn={globalFilterFn}
      filterContent={filterBar.controls}
      filterChips={filterBar.chips}
      statsBar={renderStats}
    />
    )}
    <AddItemModal
      open={showAddRow}
      onOpenChange={setShowAddRow}
      title="Add key date"
      description="Add a manual milestone, deadline, or event to track alongside AI-extracted dates."
      fields={modalFields}
      onSubmit={handleAddManualItem}
    />
    <BulkConfirmDelete
      open={bulkDelete !== null}
      onOpenChange={(open) => {
        if (!open) {
          setBulkDelete(null)
          setPendingClear(null)
        }
      }}
      count={bulkDelete?.ids.length ?? 0}
      aiCount={bulkDelete?.aiCount ?? 0}
      itemNoun="date"
      onConfirm={() => {
        void (async () => {
          if (!bulkDelete) return
          await bulkDeleteItems(bidId, 'key_dates', bulkDelete.ids)
          pendingClear?.()
          setBulkDelete(null)
          setPendingClear(null)
        })()
      }}
    />
    </>
  )
}
